import { Check, Copy, Download, Plus } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { wordpressPluginUrl } from "../config";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { iframeSnippet, wordpressModalShortcode, wordpressShortcode } from "../lib/snippets";
import { Button, CodeBlock, CopyButton, Segmented, useCopy } from "./ui";

/** How to put the calculator on a website: the WordPress plugin, or an iframe anywhere. Without a key the WordPress
 * path still shows (Connect to Solar OS creates one); manual setup and the iframe offer to create a key. */
export function InstallSnippets({ embedKey, onCreateKey, creatingKey }: {
  embedKey?: string | null;
  onCreateKey?: () => void;
  creatingKey?: boolean;
}) {
  const [tab, setTab] = useState<"wordpress" | "iframe">("wordpress");
  const [manual, setManual] = useState(false);
  const t = useT();
  const intl = useIntlShape();
  const code = (c: ReactNode[]) => <code dir="ltr">{c}</code>;
  return (
    <div className="stack" style={{ gap: 12 }}>
      <Segmented
        value={tab}
        onChange={setTab}
        options={[
          { value: "wordpress", label: "WordPress" },
          { value: "iframe", label: t("website.install.tab.iframe") },
        ]}
      />
      {tab === "wordpress" ? (
        <div className="stack" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 12 }}>
            <a className="btn btn-primary" href={wordpressPluginUrl()} download>
              <Download /> {t("website.install.wordpress.download")}
            </a>
          </div>
          <p className="muted" style={{ margin: 0 }}>
            {intl.formatMessage({ id: "website.install.wordpress.connect" }, { b: (c) => <b>{c}</b>, hasKey: embedKey ? "yes" : "no" })}
          </p>
          <div className="spread">
            <span className="muted small">
              {intl.formatMessage({ id: "website.install.wordpress.shortcode" }, { code, shortcode: wordpressShortcode, modal: wordpressModalShortcode })}
            </span>
            <CopyButton text={wordpressShortcode} label={t("website.install.wordpress.copyShortcode")} />
          </div>
          {manual && embedKey && (
            <div className="stack" style={{ gap: 6 }}>
              <CopyField label={t("website.install.embedKey")} copyLabel={t("website.install.copyEmbedKey")} value={embedKey} />
              <span className="muted small">{t("website.install.wordpress.manualHint")}</span>
            </div>
          )}
          {manual && !embedKey && <NeedsKey text={t("website.install.needsKey.manual")} onCreateKey={onCreateKey} creatingKey={creatingKey} />}
          <div className="row" style={{ gap: 14 }}>
            {!manual && (
              <button type="button" className="link-btn small" style={{ fontWeight: 500 }} onClick={() => setManual(true)}>
                {t("website.install.wordpress.manual")}
              </button>
            )}
          </div>
          <PreviousVersions />
        </div>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          <p className="muted">{t("website.install.iframe.hint")}</p>
          {embedKey ? (
            <CodeBlock code={iframeSnippet(embedKey)} />
          ) : (
            <NeedsKey text={t("website.install.needsKey.iframe")} onCreateKey={onCreateKey} creatingKey={creatingKey} />
          )}
        </div>
      )}
    </div>
  );
}

function NeedsKey({ text, onCreateKey, creatingKey }: { text: string; onCreateKey?: () => void; creatingKey?: boolean }) {
  const t = useT();
  return (
    <div className="spread" style={{ gap: 10 }}>
      <span className="muted small">{text}</span>
      {onCreateKey && (
        <Button size="sm" icon={<Plus />} loading={creatingKey} onClick={onCreateKey}>
          {t("website.install.createKey")}
        </Button>
      )}
    </div>
  );
}

/** A read-only value with its own Copy button; clicking the field selects and copies it too. */
function CopyField({ label, copyLabel, value }: { label: string; copyLabel: string; value: string }) {
  const [copied, copy] = useCopy();
  const t = useT();
  return (
    <div className="stack" style={{ gap: 4 }}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
      <div className="input-group">
        <input
          className="input mono"
          dir="ltr"
          readOnly
          value={value}
          aria-label={label}
          onClick={(e) => {
            e.currentTarget.select();
            copy(value);
          }}
          style={{ cursor: "copy" }}
        />
        <button type="button" className="btn btn-sm" onClick={() => copy(value)} aria-label={copyLabel}>
          {copied ? <Check /> : <Copy />} {copied ? t("website.action.copied") : t("website.action.copy")}
        </button>
      </div>
    </div>
  );
}

interface PluginVersion {
  version: string;
  date: string;
  url: string;
}

/** Up to 10 published plugin versions from `plugin-info.json` next to the download (quietly absent on failure). */
function usePluginVersions(open: boolean): PluginVersion[] | null {
  const [versions, setVersions] = useState<PluginVersion[] | null>(null);
  useEffect(() => {
    if (!open || versions) return;
    let cancelled = false;
    const info = new URL("/plugin-info.json", wordpressPluginUrl());
    fetch(info.toString(), { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((body: { versions?: Record<string, unknown>[] }) => {
        const list = (Array.isArray(body.versions) ? body.versions : []).slice(0, 10).flatMap((v) => {
          const version = typeof v.version === "string" ? v.version : "";
          const href = [v.download, v.url, v.download_url].find((x): x is string => typeof x === "string");
          if (!version || !href) return [];
          const date = [v.released, v.date, v.released_at].find((x): x is string => typeof x === "string") ?? "";
          return [{ version, date: date.slice(0, 10), url: new URL(href, info).toString() }];
        });
        if (!cancelled) setVersions(list);
      })
      .catch(() => !cancelled && setVersions([]));
    return () => {
      cancelled = true;
    };
  }, [open, versions]);
  return versions;
}

function PreviousVersions() {
  const [open, setOpen] = useState(false);
  const versions = usePluginVersions(open);
  const t = useT();
  const intl = useIntlShape();
  const day = (d: string) => (/^\d{4}-\d{2}-\d{2}$/.test(d) ? intl.formatDate(`${d}T12:00:00Z`, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }) : d);
  return (
    <div className="wp-versions">
      <button type="button" className="wp-versions-link" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {t("website.install.versions.title")} {open ? "▴" : "▾"}
      </button>
      {open && (
        <div className="wp-versions-list">
          {versions === null ? (
            <span>{t("website.action.loading")}</span>
          ) : versions.length === 0 ? (
            <span>{t("website.install.versions.none")}</span>
          ) : (
            versions.map((v) => (
              <a key={v.version} href={v.url} download>
                <span className="mono" dir="ltr">{v.version}</span>
                <span>{day(v.date)}</span>
                <span>{t("website.action.download")}</span>
              </a>
            ))
          )}
        </div>
      )}
    </div>
  );
}
